import { v4 as uuid } from "uuid";
import { getLocalTodayDateOnly } from "@helpers/date";
import { z } from "zod";
import {
    DEFAULT_SCHEDULE_TEMPLATE_GROUP,
    getAllocationPercentUnits,
    getRemainingAllocationPercent,
    MAX_RECOGNITION_COMPONENTS,
    MAX_SCHEDULE_TEMPLATE_DESCRIPTION_LENGTH,
    MAX_SCHEDULE_TEMPLATE_GROUP_LENGTH,
    MAX_SCHEDULE_TEMPLATE_NAME_LENGTH,
    splitAllocationPercents,
} from "@common/constants/domains/accounting/schedules";
import {
    CreateScheduleTemplateBodyInput,
    ScheduleTemplateResponse,
    refineScheduleRecognitionConfig,
} from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import {
    SCHEDULE_POLICY_KIND_MODES,
    SchedulePolicyKind,
    toSchedulePolicyKind,
} from "@common/helpers/domains/accounting/schedulePolicyKind";
import { Account } from "@common/types/domains/accounting/account";
import {
    RecognitionAnchor,
    hasTermRecognitionComponent,
    isTermRecognitionMethod,
    RecognitionMethod,
    ScheduleTermUnit,
} from "@common/types/domains/accounting/schedule";
import {
    makeScheduleMilestone,
    ScheduleMilestoneForm,
    toMilestoneBodies,
    toMilestoneBody,
} from "./scheduleMilestoneFormHelpers";
import { scheduleComponentRowFormSchema } from "./scheduleComponentRow.schema";
import { milestoneRowsFormSchema } from "./scheduleMilestoneRow.schema";
import type { MilestoneExampleDateForm } from "./scheduleMilestonePreviewHelpers";

export const DEFAULT_SCHEDULE_PREVIEW_AMOUNT = 10_000;

export type WorkspaceAccountWithEntity = Account & { entityId: number };

export interface ScheduleAccountBlockForm {
    key: string;
    entityIds: number[];
    holdingAccountName: string | null;
    recognitionAccountName: string | null;
}

export interface ScheduleComponentForm {
    key: string;
    method: RecognitionMethod;
    anchor: RecognitionAnchor;
    offsetDays: number;
    allocationPercent: number;
}

export enum ScheduleAllocationMode {
    equalSplit = "equalSplit",
    remainderLast = "remainderLast",
    manual = "manual",
}

export interface ScheduleAllocationState {
    components: ScheduleComponentForm[];
    allocationMode: ScheduleAllocationMode;
}

export interface ScheduleTemplateFormValues {
    kind: SchedulePolicyKind;
    name: string;
    description: string;
    group: string;
    termLength: number;
    termUnit: ScheduleTermUnit;
    components: ScheduleComponentForm[];
    allocationMode: ScheduleAllocationMode;
    milestones: ScheduleMilestoneForm[];
    accountBlocks: ScheduleAccountBlockForm[];
    exampleAmount: number;
    exampleSourceDate?: string;
    exampleSpecificDate?: string;
    /** The day the milestone preview counts from; the policy keeps no real source. */
    milestoneExampleSourceDate: string;
    /** Example days typed over resolved ones, by milestone row key. */
    milestoneExampleDates: MilestoneExampleDateForm[];
}

export function makeScheduleComponent(
    overrides?: Partial<Omit<ScheduleComponentForm, "key">>,
): ScheduleComponentForm {
    return {
        key: uuid(),
        method: RecognitionMethod.straightLine,
        anchor: RecognitionAnchor.transactionDate,
        offsetDays: 0,
        allocationPercent: 100,
        ...overrides,
    };
}

export function detectAllocationMode(
    allocationPercents: number[],
): ScheduleAllocationMode {
    if (allocationPercents.length <= 1) {
        return ScheduleAllocationMode.equalSplit;
    }
    const equalPercents = splitAllocationPercents(allocationPercents.length);
    const isEqualSplit = allocationPercents.every(
        (percent, percentIndex) =>
            getAllocationPercentUnits([percent]) ===
            getAllocationPercentUnits([equalPercents.at(percentIndex) ?? 0]),
    );
    return isEqualSplit
        ? ScheduleAllocationMode.equalSplit
        : ScheduleAllocationMode.manual;
}

export function rewriteLastAllocationPercent(
    components: ScheduleComponentForm[],
): ScheduleComponentForm[] {
    if (components.length === 0) {
        return components;
    }
    const lastIndex = components.length - 1;
    const lastPercent = Math.max(
        getRemainingAllocationPercent(
            components
                .slice(0, lastIndex)
                .map((component) => component.allocationPercent),
        ),
        0,
    );
    const last = components[lastIndex];
    if (
        getAllocationPercentUnits([last.allocationPercent]) ===
        getAllocationPercentUnits([lastPercent])
    ) {
        return components;
    }
    return components.map((component, index) =>
        index === lastIndex
            ? { ...component, allocationPercent: lastPercent }
            : component,
    );
}

export function setScheduleComponentAllocation(
    options: ScheduleAllocationState & {
        index: number;
        allocationPercent: number;
    },
): ScheduleAllocationState {
    const { components, allocationMode, index, allocationPercent } = options;
    const lastIndex = components.length - 1;
    if (components.length <= 1 || index < 0 || index >= lastIndex) {
        return { components, allocationMode };
    }
    const editedComponents = components.map((component, componentIndex) =>
        componentIndex === index
            ? { ...component, allocationPercent }
            : component,
    );
    return {
        components: rewriteLastAllocationPercent(editedComponents),
        allocationMode: ScheduleAllocationMode.remainderLast,
    };
}

function withEqualAllocationPercents(
    components: ScheduleComponentForm[],
): ScheduleComponentForm[] {
    const equalPercents = splitAllocationPercents(components.length);
    return components.map((component, componentIndex) => ({
        ...component,
        allocationPercent: equalPercents.at(componentIndex) ?? 0,
    }));
}

export function addScheduleComponent(
    state: ScheduleAllocationState,
): ScheduleAllocationState {
    const { components } = state;
    if (components.length === 1) {
        return {
            components: withEqualAllocationPercents([
                ...components,
                makeScheduleComponent({
                    method: RecognitionMethod.pointInTime,
                    anchor: RecognitionAnchor.transactionDate,
                }),
            ]),
            allocationMode: ScheduleAllocationMode.remainderLast,
        };
    }
    const newComponent = makeScheduleComponent({
        method: RecognitionMethod.pointInTime,
        anchor: RecognitionAnchor.transactionDate,
        allocationPercent: Math.max(
            getRemainingAllocationPercent(
                components.map((component) => component.allocationPercent),
            ),
            0,
        ),
    });
    return {
        components: [...components, newComponent],
        allocationMode: ScheduleAllocationMode.remainderLast,
    };
}

export function removeScheduleComponent(
    state: ScheduleAllocationState & { index: number },
): ScheduleAllocationState {
    const { components, allocationMode, index } = state;
    if (components.length <= 1) {
        return { components, allocationMode };
    }
    const remainingComponents = components.filter(
        (_, componentIndex) => componentIndex !== index,
    );
    if (remainingComponents.length === 1) {
        return {
            components: withEqualAllocationPercents(remainingComponents),
            allocationMode: ScheduleAllocationMode.equalSplit,
        };
    }
    return {
        components: rewriteLastAllocationPercent(remainingComponents),
        allocationMode: ScheduleAllocationMode.remainderLast,
    };
}

export function makeScheduleAccountBlock(
    overrides?: Partial<Omit<ScheduleAccountBlockForm, "key">>,
): ScheduleAccountBlockForm {
    return {
        key: uuid(),
        entityIds: [],
        holdingAccountName: null,
        recognitionAccountName: null,
        ...overrides,
    };
}

export function getDefaultScheduleAccountEntityIds({
    initialEntityId,
    businessEntityIds,
}: {
    initialEntityId?: number;
    businessEntityIds: number[];
}): number[] {
    if (initialEntityId == null) {
        return businessEntityIds;
    }
    return [initialEntityId];
}

export function getEmptyScheduleFormValues(
    overrides?: Partial<ScheduleTemplateFormValues>,
): ScheduleTemplateFormValues {
    return {
        kind: SchedulePolicyKind.deferredRevenue,
        name: "",
        description: "",
        group: DEFAULT_SCHEDULE_TEMPLATE_GROUP,
        termLength: 12,
        termUnit: ScheduleTermUnit.months,
        components: [makeScheduleComponent()],
        allocationMode: ScheduleAllocationMode.equalSplit,
        milestones: [],
        accountBlocks: [makeScheduleAccountBlock()],
        exampleAmount: DEFAULT_SCHEDULE_PREVIEW_AMOUNT,
        exampleSourceDate: undefined,
        exampleSpecificDate: undefined,
        milestoneExampleSourceDate: getLocalTodayDateOnly(),
        milestoneExampleDates: [],
        ...overrides,
    };
}

function normalizeAccountName(name: string): string {
    return name.trim().toLowerCase();
}

function findEntityAccountByName(
    accounts: WorkspaceAccountWithEntity[],
    entityId: number,
    accountName: string,
): WorkspaceAccountWithEntity | undefined {
    const normalizedName = normalizeAccountName(accountName);
    return accounts.find(
        (account) =>
            account.entityId === entityId &&
            normalizeAccountName(account.name) === normalizedName,
    );
}

export interface UnresolvedScheduleAccount {
    entityId: number;
    accountName: string;
}

export type ScheduleFormModelResult =
    | { ok: true; body: CreateScheduleTemplateBodyInput }
    | { ok: false; unresolvedAccounts: UnresolvedScheduleAccount[] };

export function formToModel(
    values: ScheduleTemplateFormValues,
    accounts: WorkspaceAccountWithEntity[],
): ScheduleFormModelResult {
    const hasTermMethod = hasTermRecognitionComponent(values.components);

    const unresolvedAccounts: UnresolvedScheduleAccount[] = [];
    const entityAccounts: CreateScheduleTemplateBodyInput["entityAccounts"] =
        [];
    for (const block of values.accountBlocks) {
        for (const entityId of block.entityIds) {
            const holdingAccount = block.holdingAccountName
                ? findEntityAccountByName(
                      accounts,
                      entityId,
                      block.holdingAccountName,
                  )
                : undefined;
            const recognitionAccount = block.recognitionAccountName
                ? findEntityAccountByName(
                      accounts,
                      entityId,
                      block.recognitionAccountName,
                  )
                : undefined;
            if (!holdingAccount) {
                unresolvedAccounts.push({
                    entityId,
                    accountName: block.holdingAccountName ?? "",
                });
            }
            if (!recognitionAccount) {
                unresolvedAccounts.push({
                    entityId,
                    accountName: block.recognitionAccountName ?? "",
                });
            }
            if (holdingAccount && recognitionAccount) {
                entityAccounts.push({
                    entityId,
                    holdingAccountId: holdingAccount.id,
                    recognitionAccountId: recognitionAccount.id,
                });
            }
        }
    }
    if (unresolvedAccounts.length > 0) {
        return { ok: false, unresolvedAccounts };
    }

    return {
        ok: true,
        body: {
            ...SCHEDULE_POLICY_KIND_MODES[values.kind],
            name: values.name,
            description:
                values.description.trim() === "" ? null : values.description,
            group: values.group,
            termLength: hasTermMethod ? values.termLength : 1,
            termUnit: hasTermMethod ? values.termUnit : ScheduleTermUnit.months,
            components: values.components.map((component) => ({
                method: component.method,
                anchor: component.anchor,
                offsetDays: isTermRecognitionMethod(component.method)
                    ? 0
                    : component.offsetDays,
                allocationPercent: component.allocationPercent,
            })),
            milestones: toMilestoneBodies(values),
            entityAccounts,
        },
    };
}

export function groupAccountRowsIntoBlocks(
    accountRows: ScheduleTemplateResponse["accountRows"],
    accounts: WorkspaceAccountWithEntity[],
): ScheduleAccountBlockForm[] {
    const accountsById = new Map(
        accounts.map((account) => [account.id, account]),
    );
    const blocksByNamePair = new Map<string, ScheduleAccountBlockForm>();
    const blocks: ScheduleAccountBlockForm[] = [];

    for (const accountRow of accountRows) {
        const holdingAccount = accountsById.get(accountRow.holdingAccountId);
        const recognitionAccount = accountsById.get(
            accountRow.recognitionAccountId,
        );
        if (!holdingAccount || !recognitionAccount) {
            blocks.push(
                makeScheduleAccountBlock({
                    entityIds: [accountRow.entityId],
                    holdingAccountName: holdingAccount?.name ?? null,
                    recognitionAccountName: recognitionAccount?.name ?? null,
                }),
            );
            continue;
        }
        const namePairKey = `${normalizeAccountName(holdingAccount.name)}|${normalizeAccountName(recognitionAccount.name)}`;
        const existingBlock = blocksByNamePair.get(namePairKey);
        if (existingBlock) {
            existingBlock.entityIds.push(accountRow.entityId);
            continue;
        }
        const block = makeScheduleAccountBlock({
            entityIds: [accountRow.entityId],
            holdingAccountName: holdingAccount.name,
            recognitionAccountName: recognitionAccount.name,
        });
        blocksByNamePair.set(namePairKey, block);
        blocks.push(block);
    }

    return blocks;
}

export function modelToForm(
    template: ScheduleTemplateResponse,
    accounts: WorkspaceAccountWithEntity[],
): ScheduleTemplateFormValues {
    return {
        kind:
            toSchedulePolicyKind(template) ??
            SchedulePolicyKind.deferredRevenue,
        name: template.name,
        description: template.description ?? "",
        group: template.group,
        termLength: template.termLength,
        termUnit: template.termUnit,
        components: template.components.map((component) => ({
            key: uuid(),
            method: component.method,
            anchor: component.anchor,
            offsetDays: component.offsetDays,
            allocationPercent: component.allocationPercent,
        })),
        allocationMode: detectAllocationMode(
            template.components.map((component) => component.allocationPercent),
        ),
        milestones: template.milestones.map((milestone) =>
            makeScheduleMilestone(toMilestoneBody(milestone)),
        ),
        accountBlocks: groupAccountRowsIntoBlocks(
            template.accountRows,
            accounts,
        ),
        exampleAmount: DEFAULT_SCHEDULE_PREVIEW_AMOUNT,
        exampleSourceDate: undefined,
        exampleSpecificDate: undefined,
        milestoneExampleSourceDate: getLocalTodayDateOnly(),
        milestoneExampleDates: [],
    };
}

export const scheduleTemplateFormSchema = z
    .object({
        kind: z.nativeEnum(SchedulePolicyKind),
        name: z
            .string()
            .trim()
            .min(1, "Policy name is required")
            .max(MAX_SCHEDULE_TEMPLATE_NAME_LENGTH),
        description: z
            .string()
            .trim()
            .max(MAX_SCHEDULE_TEMPLATE_DESCRIPTION_LENGTH),
        group: z
            .string()
            .trim()
            .min(1, "Group is required")
            .max(MAX_SCHEDULE_TEMPLATE_GROUP_LENGTH),
        termLength: z
            .number({ invalid_type_error: "Enter a term length" })
            .int("Term length must be a whole number")
            .min(1, "Term length must be at least 1"),
        termUnit: z.nativeEnum(ScheduleTermUnit),
        components: z
            .array(scheduleComponentRowFormSchema)
            .min(1, "Add at least one component")
            .max(MAX_RECOGNITION_COMPONENTS),
        allocationMode: z.nativeEnum(ScheduleAllocationMode),
        milestones: milestoneRowsFormSchema,
        accountBlocks: z
            .array(
                z.object({
                    key: z.string(),
                    entityIds: z
                        .array(z.number().int().positive())
                        .min(1, "Select at least one entity"),
                    holdingAccountName: z.string({
                        invalid_type_error: "Select an account",
                    }),
                    recognitionAccountName: z.string({
                        invalid_type_error: "Select an account",
                    }),
                }),
            )
            .min(1, "Add at least one accounts row"),
        exampleAmount: z.number().positive(),
    })
    .superRefine((value, ctx) => {
        refineScheduleRecognitionConfig(value, ctx);

        const seenEntityIds = new Set<number>();
        for (const [index, block] of value.accountBlocks.entries()) {
            for (const entityId of block.entityIds) {
                if (seenEntityIds.has(entityId)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["accountBlocks", index, "entityIds"],
                        message:
                            "An entity can only appear in one accounts row",
                    });
                }
                seenEntityIds.add(entityId);
            }
            if (
                block.holdingAccountName != null &&
                block.recognitionAccountName != null &&
                normalizeAccountName(block.holdingAccountName) ===
                    normalizeAccountName(block.recognitionAccountName)
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["accountBlocks", index, "recognitionAccountName"],
                    message:
                        "Recognition account must differ from the holding account",
                });
            }
        }
    });
